/**
 * Pure new-module construction kernel for the sidebar — extracted verbatim
 * (behavior-preserving) from the `handleCreateModule` body in
 * layout/Sidebar.tsx. Isolates the position computation and the Module-object
 * shape so the create flow can be characterization-tested without React, query
 * client, or the async addModule side effects.
 *
 * The stateful double-submit guard (`isCreatingModuleRef` + `isCreatingModule`)
 * and the await/finally lifecycle live in `useModuleCreation`; this file holds
 * only the deterministic value math.
 */

import { nanoid } from 'nanoid';
import type { Module } from '@/types/modules';
import { MODULE_TYPE_TO_OBJECT } from './constants';

/**
 * Compute the position for a newly inserted module: one less than the smallest
 * existing position (so the new module sorts to the top), with a floor of 0
 * matching the inline `reduce((min, m) => ... , 0)` seed. Verbatim port.
 */
export function computeNewModulePosition(modules: ReadonlyArray<{ position?: number }>): number {
  const minPos = modules.reduce(
    (min, m) => (typeof m.position === 'number' && m.position < min ? m.position : min),
    0,
  );
  return minPos - 1;
}

/**
 * Build the new Module object exactly as `handleCreateModule` does, including
 * the conditional `objectType` spread driven by MODULE_TYPE_TO_OBJECT. `id` is
 * a fresh nanoid (the only non-deterministic field).
 */
export function buildNewModule(args: {
  conversationId: string;
  type: string;
  label: string;
  existingModules: ReadonlyArray<{ position?: number }>;
}): Module {
  const { conversationId, type, label, existingModules } = args;
  const objType = MODULE_TYPE_TO_OBJECT[type];
  return {
    id: nanoid(),
    conversation_id: conversationId,
    type: type as Module['type'],
    label,
    position: computeNewModulePosition(existingModules),
    size: 'normal',
    ...(objType ? { objectType: objType as Module['objectType'] } : {}),
  } as Module;
}
